import React, { useState } from 'react';
import { X, Save, Bus } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Bus as BusType, BusType as BusTypeModel, BusAmenity, BusFeature, CreateBusForm, UpdateBusForm } from '../../types';

interface BusFormProps {
  bus?: BusType | null;
  busTypes: BusTypeModel[];
  amenities: BusAmenity[];
  features: BusFeature[];
  onSubmit: (data: CreateBusForm | UpdateBusForm) => void;
  onClose: () => void;
}

export const BusForm: React.FC<BusFormProps> = ({
  bus,
  busTypes: _busTypes,
  amenities: _amenities,
  features,
  onSubmit,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    busNumber: bus?.busNumber || '',
    busTypeId: bus?.busType?.id || '',
    operatorName: bus?.operatorName || '',
    totalSeats: bus?.totalSeats || 50,
    amenities: bus?.amenities?.map(a => a?.id).filter(Boolean) as string[] || [],
    features: bus?.features?.map(f => f?.id).filter(Boolean) as string[] || [],
    fuelType: bus?.fuelType || 'diesel' as 'diesel' | 'electric' | 'hybrid',
    yearOfManufacture: bus?.yearOfManufacture || new Date().getFullYear(),
    registrationNumber: bus?.registrationNumber || '',
    insuranceExpiry: bus?.insuranceExpiry?.toISOString().split('T')[0] || '',
    isActive: bus?.isActive ?? true,
    isInService: bus?.isInService ?? true,
    mileage: bus?.mileage || 0,
    // Specific amenities checkboxes
    hasWifi: bus?.amenities?.some(a => a?.name?.toLowerCase().includes('wifi')) || false,
    hasAC: bus?.amenities?.some(a => a?.name?.toLowerCase().includes('air conditioning') || a?.name?.toLowerCase().includes('ac')) || false,
    hasCharging: bus?.amenities?.some(a => a?.name?.toLowerCase().includes('charging')) || false,
    hasWashroom: bus?.amenities?.some(a => a?.name?.toLowerCase().includes('washroom') || a?.name?.toLowerCase().includes('toilet')) || false,
    hasInfotainment: bus?.amenities?.some(a => a?.name?.toLowerCase().includes('entertainment') || a?.name?.toLowerCase().includes('infotainment')) || false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.busNumber.trim()) {
      newErrors.busNumber = 'Bus number is required';
    }

    if (!formData.busTypeId) {
      newErrors.busTypeId = 'Bus type is required';
    }

    if (!formData.operatorName.trim()) {
      newErrors.operatorName = 'Operator name is required';
    }

    if (formData.totalSeats < 1 || formData.totalSeats > 100) {
      newErrors.totalSeats = 'Total seats must be between 1 and 100';
    }

    if (!formData.registrationNumber.trim()) {
      newErrors.registrationNumber = 'Registration number is required';
    }

    if (formData.yearOfManufacture < 1990 || formData.yearOfManufacture > new Date().getFullYear() + 1) {
      newErrors.yearOfManufacture = 'Year of manufacture must be between 1990 and next year';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Convert specific amenities to amenity IDs for submission
    const selectedAmenities = [];
    if (formData.hasWifi) selectedAmenities.push('wifi');
    if (formData.hasAC) selectedAmenities.push('air_conditioning');
    if (formData.hasCharging) selectedAmenities.push('charging_ports');
    if (formData.hasWashroom) selectedAmenities.push('washroom');
    if (formData.hasInfotainment) selectedAmenities.push('infotainment');

    const submitData = {
      ...formData,
      amenities: selectedAmenities,
      insuranceExpiry: formData.insuranceExpiry ? new Date(formData.insuranceExpiry) : undefined,
    };

    onSubmit(submitData);
  };

  const handleFeatureToggle = (featureId: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(id => id !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const handleSpecificAmenityToggle = (amenityType: 'hasWifi' | 'hasAC' | 'hasCharging' | 'hasWashroom' | 'hasInfotainment') => {
    setFormData(prev => ({
      ...prev,
      [amenityType]: !prev[amenityType]
    }));
  };

  const groupedFeatures = features.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {} as Record<string, BusFeature[]>);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Bus className="h-6 w-6 text-primary-600 mr-3" />
              <h2 className="text-xl font-semibold text-secondary-900">
                {bus ? 'Edit Bus' : 'Add New Bus'}
              </h2>
            </div>
            <Button variant="outline" onClick={onClose} className="p-2">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Bus Number *
                </label>
                <Input
                  value={formData.busNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, busNumber: e.target.value }))}
                  placeholder="e.g., WIC-001"
                  className={errors.busNumber ? 'border-red-500' : ''}
                />
                {errors.busNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.busNumber}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Registration Number *
                </label>
                <Input
                  value={formData.registrationNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, registrationNumber: e.target.value }))}
                  placeholder="e.g., GR-1234-21"
                  className={errors.registrationNumber ? 'border-red-500' : ''}
                />
                {errors.registrationNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.registrationNumber}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Bus Type *
                </label>
                <select
                  value={formData.busTypeId}
                  onChange={(e) => setFormData(prev => ({ ...prev, busTypeId: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${errors.busTypeId ? 'border-red-500' : 'border-secondary-300'
                    }`}
                >
                  <option value="">Select Bus Type</option>
                  <option value="e-coach">E-Coach</option>
                  <option value="standard-bus">Standard Bus</option>
                  <option value="van">Van</option>
                  <option value="mini-van">Mini-Van</option>
                </select>
                {errors.busTypeId && (
                  <p className="mt-1 text-sm text-red-600">{errors.busTypeId}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Operator Name *
                </label>
                <Input
                  value={formData.operatorName}
                  onChange={(e) => setFormData(prev => ({ ...prev, operatorName: e.target.value }))}
                  placeholder="e.g., WoteInterCity"
                  className={errors.operatorName ? 'border-red-500' : ''}
                />
                {errors.operatorName && (
                  <p className="mt-1 text-sm text-red-600">{errors.operatorName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Total Seats *
                </label>
                <Input
                  type="number"
                  value={formData.totalSeats}
                  onChange={(e) => setFormData(prev => ({ ...prev, totalSeats: parseInt(e.target.value) || 0 }))}
                  min="1"
                  max="100"
                  className={errors.totalSeats ? 'border-red-500' : ''}
                />
                {errors.totalSeats && (
                  <p className="mt-1 text-sm text-red-600">{errors.totalSeats}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Fuel Type
                </label>
                <select
                  value={formData.fuelType}
                  onChange={(e) => setFormData(prev => ({ ...prev, fuelType: e.target.value as 'diesel' | 'electric' | 'hybrid' }))}
                  className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="diesel">Diesel</option>
                  <option value="electric">Electric</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Year of Manufacture *
                </label>
                <Input
                  type="number"
                  value={formData.yearOfManufacture}
                  onChange={(e) => setFormData(prev => ({ ...prev, yearOfManufacture: parseInt(e.target.value) || 0 }))}
                  min="1990"
                  max={new Date().getFullYear() + 1}
                  className={errors.yearOfManufacture ? 'border-red-500' : ''}
                />
                {errors.yearOfManufacture && (
                  <p className="mt-1 text-sm text-red-600">{errors.yearOfManufacture}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Insurance Expiry
                </label>
                <Input
                  type="date"
                  value={formData.insuranceExpiry}
                  onChange={(e) => setFormData(prev => ({ ...prev, insuranceExpiry: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Mileage (km)
                </label>
                <Input
                  type="number"
                  value={formData.mileage}
                  onChange={(e) => setFormData(prev => ({ ...prev, mileage: parseInt(e.target.value) || 0 }))}
                  min="0"
                />
              </div>
            </div>

            {/* Amenities */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-3">
                Amenities
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <label className="flex items-center space-x-2 cursor-pointer p-3 border border-secondary-200 rounded-lg hover:bg-secondary-50">
                  <input
                    type="checkbox"
                    checked={formData.hasWifi}
                    onChange={() => handleSpecificAmenityToggle('hasWifi')}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-secondary-700">WiFi</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer p-3 border border-secondary-200 rounded-lg hover:bg-secondary-50">
                  <input
                    type="checkbox"
                    checked={formData.hasAC}
                    onChange={() => handleSpecificAmenityToggle('hasAC')}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-secondary-700">Air Conditioning</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer p-3 border border-secondary-200 rounded-lg hover:bg-secondary-50">
                  <input
                    type="checkbox"
                    checked={formData.hasCharging}
                    onChange={() => handleSpecificAmenityToggle('hasCharging')}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-secondary-700">Charging Ports</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer p-3 border border-secondary-200 rounded-lg hover:bg-secondary-50">
                  <input
                    type="checkbox"
                    checked={formData.hasWashroom}
                    onChange={() => handleSpecificAmenityToggle('hasWashroom')}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-secondary-700">Washroom</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer p-3 border border-secondary-200 rounded-lg hover:bg-secondary-50">
                  <input
                    type="checkbox"
                    checked={formData.hasInfotainment}
                    onChange={() => handleSpecificAmenityToggle('hasInfotainment')}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-secondary-700">Infotainment</span>
                </label>
              </div>
            </div>

            {/* Features */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-3">
                Features
              </label>
              <div className="space-y-4">
                {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => (
                  <div key={category}>
                    <h4 className="text-sm font-medium text-secondary-600 mb-2 capitalize">
                      {category}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {categoryFeatures.map(feature => (
                        <label
                          key={feature.id}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.features.includes(feature.id)}
                            onChange={() => handleFeatureToggle(feature.id)}
                            className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-secondary-700">{feature.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center space-x-6">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-secondary-700">Active</span>
              </label>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.isInService}
                  onChange={(e) => setFormData(prev => ({ ...prev, isInService: e.target.checked }))}
                  className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-secondary-700">In Service</span>
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-secondary-200">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" className="flex items-center">
                <Save className="h-4 w-4 mr-2" />
                {bus ? 'Update Bus' : 'Create Bus'}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};
